import { Box, ButtonBase, useMediaQuery} from "@mui/material";
import { useTheme } from "@mui/material";
import { tokens } from "../../../../../theme";
import Shbutton from "../../../../theme/buttons/Shbutton";
import SaveIcon from '@mui/icons-material/Save';

import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { getScrollParent } from "../../../../../utils/scroll";
import CropFreeIcon from '@mui/icons-material/CropFree';
import ButtonGroup from "../../../../theme/buttons/ButtonGroup";

import { useTranslation } from "react-i18next";
import { useGetShippersSelectQuery, useGetShippersSelectWithProfilesQuery } from "../../../../../newapi/shipper/shipperSlice";
import { useGetInvoiceProductsSelectQuery } from "../../../../../newapi/financial/invoiceProductSlice";
import { useGetCostFromShipperQuery, useAddOrUpdateCostFromShipperMutation } from "../../../../../newapi/financial/costSlice";
import CostRule from "../../../../Cost/CostRule";

import { DndContext, KeyboardSensor, MouseSensor, PointerSensor, TouchSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import Heading from "../../../../theme/text/Heading";
import { useGetCarriersTypesQuery } from "../../../../../newapi/carrier/carrierSlice";
import { countriesFromLocale } from "../../../../../utils/countries";
import { useSelector } from "react-redux";
import AddIcon from '@mui/icons-material/Add';
import { showToastMessage, showToastMessagePostRequest, showToastMessageRequestError } from "../../../../../utils/toasts";
import MAddCostProduct from "../../../Modals/MAddCostProduct";
import MMoveCostRule from "../../../Modals/MMoveCostRule";
import { useEditStreamRulesMutation, useGetStreamRulesQuery } from "../../../../../newapi/global/stream/streamSlice";
import { selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from '../../../../../utils/permissions';
import { useDropDownFetchData } from "../../../../Streams/Conditions/ConditionSettings";
import { useGetSettingsQuery } from "../../../../../newapi/global/generalApiSlice";

const GridActions = (props) => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <Box sx={{
            display:"flex",
            justifyContent:"space-between",
            width:1,
            // justifyContent:"flex-end",
            alignItems:"center",
            // borderRadius:6,
            overflow:"hidden",
            "@media screen and (max-width: 47.9375em)" : {
                // pl:1.5,
                justifyContent:"flex-end",
            }
        }}>
            <Box
                sx={{
                    width:500,
                    ml:5,
                    "@media screen and (max-width: 47.9375em)" : {
                        // pl:1.5,
                        display:"none"
                    }
                }}
            >
                {props.hasModifyPermission && (
                <Shbutton
                    block
                    onClick={() => props.handleOpen()}
                    className={"group-btn space-end-icon"}
                    // onClick={props.action}
                    color={"primaryGrey"}
                    variant="contained"
                    endIcon={<AddIcon />}
                >
                    {t('create_rule')}
                </Shbutton>
                )}
            </Box>
            <ButtonGroup version={2}>
                <Box
                    sx={{
                        width:1,
                        flex:1,
                        "@media screen and (min-width: 47.9375em)" : {
                            display:"none"
                        },
                        mx:"auto",
                    }}
                >
                <Shbutton
                    onClick={() => props.handleOpen()}
                    className={"group-btn"}
                    // onClick={props.action}
                    color={"primaryGrey"}
                    variant="contained"
                    endIcon={<AddIcon />}
                >
                    {t('create_rule')}
                </Shbutton>
                </Box>
                {props.hasModifyPermission && (
                <Shbutton
                    isLoading={props.isLoading}
                    onClick={() => props.saveCostRules()}
                    className={"group-btn"}
                    // onClick={props.action}
                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                    variant="contained"
                    endIcon={<SaveIcon />}
                >
                    {t('save')}
                </Shbutton>
                )}
            </ButtonGroup>
        </Box>
    );
};

const Costs = (props) => {
    const  { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    
    const { data: invoiceProducts } = useGetInvoiceProductsSelectQuery();
    
    const language = useSelector((state) => state.settings.language);
    // Memoized so the prop ref is stable across the virtualizer's scroll re-renders,
    // otherwise every CostRule would re-render even though it's memoized.
    const countries = useMemo(() => countriesFromLocale(language, true).map(country => ({
        id: country.alpha2,
        name: country.name
    })), [language]);
    
    const [addOrUpdateCostFromShipper, {isLoading: isEditCostRulesLoading}] = useAddOrUpdateCostFromShipperMutation();

    const hasModifyPermission = useSelector((state) => 
        selectUserPermission(state, [UserPrivileges.COST_MANAGE])
    );

    const {data, isLoading,  isFetching, isError,refetch} = useGetCostFromShipperQuery(props.shipperId,        {
        skip: !props.shipperId
    });

    const [listOptions, setListOptions] = useState({});
    const { data: fetchedListOptions, isLoading:isListLoading, error, allDataReady } = useDropDownFetchData(props.shipperId);

    useEffect(() => {
        if (allDataReady) {
            setListOptions(fetchedListOptions);
        }
    }, [allDataReady]);

    const [costRules, setCostRules] = useState([]); // Ensure each rule has a unique identifier
    const tempIdCounter = useRef(-1); // Counter for generating unique temporary IDs (negative to avoid collision with server IDs)

    useEffect(() => {
        if (!isLoading && !isError && data) {
            setCostRules(data.costRules)
        }
    }, [data, isLoading, isError]);

    const saveCostRules = async () => {
        const newRules = costRules?.map((rule, index) => {
            return {
                ...rule, // Spread the properties of the current rule
                id: rule.id < 0 ? 0 : rule.id, // Convert temporary negative IDs back to 0 for new rules
                priority: parseInt(index), // Assign the new priority value
            };
        });

        try {
            if (isEditCostRulesLoading) return;
            // If validation is successful, proceed with the addSupplier call
            const data = await addOrUpdateCostFromShipper({shipperId: props.shipperId,costRules: newRules}).unwrap();
            showToastMessagePostRequest(t, data);
            setCostRules(newRules);
        } catch (err) {
            showToastMessageRequestError(t("create_failed"), t, err);
        }
     }

     const [addModalOpen, setAddModalOpen] = useState(false);
     const addModal = {
         open: addModalOpen,
         setOpen: setAddModalOpen
     }
 
     const handleClose = (addRule, selectedInvoiceProductId) => {
        setAddModalOpen(false);
        if (addRule) {
            const highestPriority = costRules.reduce((maxPriority, rule) => Math.max(maxPriority, parseInt(rule.priority, 10)), 0);
            const tempId = tempIdCounter.current;
            tempIdCounter.current -= 1; // Decrement for next use
            setCostRules(prevRules => [
                ...prevRules,
                {
                    id: tempId,
                    priority: (highestPriority + 1).toString(),
                    invoiceProductId: selectedInvoiceProductId,
                    conditions: []
                }
            ]);
        }
    };

    const { data: eCheckSettings} = useGetSettingsQuery("invoicing");
    const invoiceFollowUpShipment = eCheckSettings?.find(setting => setting.key === 'invoicing_follow_up_shipment')?.value === "true";

    useEffect(() => {
        props.replaceView({
            ...props.config,
            mobileActions:<GridActions isLoading={isEditCostRulesLoading} saveCostRules={saveCostRules} handleOpen={() => setAddModalOpen(true)} hasModifyPermission={hasModifyPermission}></GridActions>,
        });

        return () => {
            // console.log("unmounting warehouse");
        }
    },[saveCostRules]);
    
    const [active, setActive] = useState(null)
    
    const onDragEnd = event => {
    const {active, over} = event;
        if(over === null) return;
        if(active.id === over.id) return;
        setCostRules(rules => {
            const oldIndex = costRules?.findIndex((rule) => rule.id === active.id);
            const newIndex = costRules?.findIndex((rule) => rule.id === over.id);
            return arrayMove(costRules,oldIndex,newIndex);
        });

        setActive(null);
    }
    
    const pointerSensor = useSensor(PointerSensor, {
        activationConstraint: {
            distance: 2
        }
    })
    const mouseSensor = useSensor(MouseSensor, {
        // Require the mouse to move by 10 pixels before activating
        activationConstraint: {
            distance: 10,
        },
    });
    const touchSensor = useSensor(TouchSensor, {
        // Press delay of 250ms, with tolerance of 5px of movement
        activationConstraint: {
            delay: 250,
            tolerance: 5,
        },
    });

    //   const touchSensor = useSensor(TouchSensor);
    const keyboardSensor = useSensor(KeyboardSensor)
    
    const sensors = useSensors(
        mouseSensor,
        touchSensor,
        keyboardSensor,
        pointerSensor
    )

    const isMobileWidth = useMediaQuery("(max-width:47.9375em)");
    // Touchscreen laptops, pen tablets and remote desktop sessions report touch support while
    // still being driven by a mouse, so a fine primary pointer keeps drag and drop available.
    const hasFinePointer = useMediaQuery("(pointer: fine)");
    const [disableDnD, setDisableDnD] = useState(false);
    useEffect(() => {
        const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
        setDisableDnD(isMobileWidth || (isTouchDevice && !hasFinePointer));
    }, [isMobileWidth, hasFinePointer]);

    const [stepModal, setStepModal] = useState({ open: false, fromIndex: null });

    // Virtualization: keep dnd-kit (SortableContext still gets every id) but only
    // render the visible window so clients with hundreds of rules stay performant.
    // The list is its OWN bounded scroll container (height computed from the viewport),
    // so it never depends on the unbounded sidepanel-body height chain.
    const scrollRef = useRef(null);
    const [maxHeight, setMaxHeight] = useState(0);

    // id -> invoice product lookup, so name resolution is O(1) and computed once
    // (not a .find per row per scroll frame).
    const invoiceProductMap = useMemo(() => {
        const map = new Map();
        (invoiceProducts ?? []).forEach(ip => map.set(ip.id, ip));
        return map;
    }, [invoiceProducts]);

    const visibleRules = useMemo(
        () => (costRules ?? [])
            .map((rule, originalIndex) => ({ rule, originalIndex }))
            .filter(({ rule }) => invoiceProductMap.has(rule.invoiceProductId)),
        [costRules, invoiceProductMap]
    );

    useLayoutEffect(() => {
        const recalc = () => {
            if (!scrollRef.current) return;
            // Bound the list to the visible area inside the panel's real scroll
            // container (the one that already scrolls today). Floored so it can
            // never collapse to 0 — that would render the whole list unwindowed.
            const scrollParent = getScrollParent(scrollRef.current);
            const elTop = scrollRef.current.getBoundingClientRect().top;
            const parentTop = scrollParent.getBoundingClientRect?.().top ?? 0;
            const parentHeight = scrollParent.clientHeight || window.innerHeight;
            const available = parentHeight - (elTop - parentTop) - 96; // room for footer
            setMaxHeight(Math.max(240, available));
        };
        recalc();
        // Re-measure after the drawer slide-in settles and once data has arrived.
        const raf = requestAnimationFrame(recalc);
        const timer = setTimeout(recalc, 350);
        window.addEventListener("resize", recalc);
        return () => {
            cancelAnimationFrame(raf);
            clearTimeout(timer);
            window.removeEventListener("resize", recalc);
        };
    }, [visibleRules.length]);

    const virtualizer = useVirtualizer({
        count: visibleRules.length,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => 240,
        overscan: 6,
    });

    return (
        <Box height={1}
            sx={{
                display:"flex",
                // position:'relative',
                // zIndex:1,
                gap:3,
                padding:3,
                "@media screen and (min-width: 48.1em)"  :{
                    marginBottom:9
                },
                "@media screen and (max-width: 47.9375em)" : {
                    // pl:1.5,
                }
            }}
        >
            {/* <Box>
                {streamRules.map((rule,index) => (
                    <Heading>{index}.</Heading>
                ))}
            </Box> */}
            
            <Box
                ref={scrollRef}
                sx={{
                    width:1,
                    height: maxHeight || undefined,
                    overflow: "auto",
                }}
            >
            {/* <DropWrapper> */}
                <DndContext
                    sensors={sensors} 
                    collisionDetection={closestCenter} 
                    autoScroll 
                    onDragEnd={onDragEnd}

                    onDragStart={({ active }) => {
                        setActive(active);
                    }}
                    onDragCancel={() => {
                    setActive(null);
                    }}
                >
                    <SortableContext items={costRules} strategy={verticalListSortingStrategy}>
                        <Box sx={{ position:"relative", width:1, height: virtualizer.getTotalSize() }}>
                            {virtualizer.getVirtualItems().map(virtualRow => {
                                const { rule, originalIndex } = visibleRules[virtualRow.index];

                                return (
                                <Box
                                    key={rule.id + '-' + JSON.stringify(rule.conditions.map(({ value, ...rest }) => rest))}
                                    data-index={virtualRow.index}
                                    ref={virtualizer.measureElement}
                                    sx={{
                                        position:"absolute",
                                        top:0,
                                        left:0,
                                        transform:`translateY(${virtualRow.start}px)`,
                                        touchAction:"none",
                                        width:1,
                                        pb:3,
                                        display: 'flex',
                                        gap:3,
                                        "@media screen and (max-width: 47.9375em)" : {
                                            gap:1.5,
                                        }
                                    }}
                                >
                                    <Box
                                        onClick={() => setStepModal({ open: true, fromIndex: originalIndex })}
                                        sx={{
                                            cursor:"pointer",
                                            borderRadius:2,
                                            px:0.5,
                                            height:"fit-content",
                                            "&:hover" : {
                                                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[200],
                                            },
                                            "@media screen and (max-width: 47.9375em)" : {
                                                position:"absolute",
                                                top:20,
                                                left:16,
                                                zIndex:4000,
                                            }
                                        }}
                                    >
                                        <Heading>{(originalIndex+1).toString() + "."}</Heading>
                                    </Box>
                                    <CostRule
                                        invoiceFollowUpShipment={invoiceFollowUpShipment}
                                        countries={countries}
                                        invoiceProducts={invoiceProducts}
                                        setCostRules={setCostRules}
                                        costRule={rule}
                                        listOptions={listOptions}
                                        index={originalIndex}
                                        isMobileWidth={isMobileWidth}
                                        likelyHeavy={costRules?.length > 40}
                                        disableDnD={disableDnD}
                                        disableDown={originalIndex === costRules.length - 1}
                                    />
                                </Box>
                                );
                            })}
                        </Box>
                    </SortableContext>
                </DndContext>
            </Box>
            <Box
                sx={{
                    position:"absolute",
                    bottom:0,
                    right:0,
                    padding:3,
                    width:1,
                    zIndex:21232231,
                    backdropFilter:"blur(12px)",
                    "@media screen and (min-width: 47.9376em) and (max-width: 63.9375em)" : {
                        padding:2,
                        paddingY:2.5,
                    },
                    "@media screen and (max-width: 47.9375em)" : {
                        display:"none"
                    }
                }}
            >
                    {/* Pass the necessary state values and setter functions to GridActions */}
                <GridActions
                    isLoading={isEditCostRulesLoading}
                    handleOpen={() => setAddModalOpen(true)}
                    saveCostRules={saveCostRules}
                    hasModifyPermission={hasModifyPermission}
                />
            </Box>
            <MAddCostProduct invoiceProducts={invoiceProducts} onOpen={() => addModal.setOpen(true)} onClose={() => addModal.setOpen(false)} open={addModal.open} handleClose={handleClose}/>
            <MMoveCostRule
                open={stepModal.open}
                fromIndex={stepModal.fromIndex}
                costRules={costRules}
                setCostRules={setCostRules}
                handleClose={() => setStepModal({ open: false, fromIndex: null })}
            />
            {/* <MAddStre */}
            {/* // <MAddSupplier onOpen={() => addModal.setOpen(true)} onClose={() => addModal.setOpen(false)} open={addModal.open} handleClose={handleClose}/> */}
            {/* </DropWrapper> */}
        </Box>
    );
}

export default Costs;

